<template>
  <div>
    <ListToolbar
      v-model:search="search"
      search-placeholder="Search tags..."
      :total="pagination.total"
      :page="pagination.page"
      :limit="pagination.limit"
      v-model:sort="sort"
      :sort-options="sortOptions"
      @reload="fetchTags"
      @page-change="goToPage"
    >
      <template #actions>
        <button v-if="$checkPermission('tags.create')" @click="openCreate" class="btn-primary">
          <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
          New Tag
        </button>
      </template>
    </ListToolbar>

    <!-- Table -->
    <div v-if="loading || items.length" :class="{ 'animate-fade-in': !loading }" class="bg-white rounded-xl border border-gray-200/60 table-scroll card-shadow">
      <table class="w-full">
        <thead>
          <tr class="bg-gray-50 border-b border-gray-100">
            <th class="text-left text-sm font-semibold text-gray-500 uppercase tracking-wider px-6 py-3.5">Name</th>
            <th class="text-left text-sm font-semibold text-gray-500 uppercase tracking-wider px-6 py-3.5 hidden md:table-cell">Created</th>
            <th class="text-right text-sm font-semibold text-gray-500 uppercase tracking-wider px-6 py-3.5">Actions</th>
          </tr>
        </thead>
        <tbody v-if="loading">
          <tr v-for="i in 6" :key="'skel-' + i" class="border-b border-gray-50">
            <td class="px-6 py-4">
              <div class="space-y-2"><div class="h-3.5 bg-gray-100 rounded skeleton-pulse" :style="{ width: (40 + i * 5) + '%' }" /></div>
            </td>
            <td class="px-6 py-4 hidden md:table-cell"><div class="h-3.5 bg-gray-100 rounded skeleton-pulse w-20" /></td>
            <td class="px-6 py-4"><div class="h-8 bg-gray-100 rounded-lg skeleton-pulse w-20 ml-auto" /></td>
          </tr>
        </tbody>
        <tbody v-else class="divide-y divide-gray-50">
          <tr v-for="item in items" :key="item._id" class="hover:bg-gray-50/50 transition-colors">
            <td class="px-6 py-4">
              <p class="text-[14px] text-gray-900">{{ item.name }}</p>
            </td>
            <td class="px-6 py-4 hidden md:table-cell">
              <span class="text-sm text-gray-500">{{ $filters.formatDatetime(item.created_at) }}</span>
            </td>
            <td class="px-6 py-4 text-right">
              <div class="flex items-center justify-end gap-1">
                <ActionsMenu :items="tagActions(item)" />
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Empty: no matches -->
    <div v-else-if="!loading && search" class="bg-white rounded-xl border border-gray-200/60 px-6 py-20 text-center card-shadow">
      <div class="w-14 h-14 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4">
        <svg class="w-6 h-6 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
      </div>
      <p class="text-base font-medium text-gray-900">No matching tags found</p>
      <p class="text-sm text-gray-500 mt-1">Try adjusting your search</p>
    </div>

    <!-- Empty: no records -->
    <div v-else-if="!loading" class="bg-white rounded-xl border border-gray-200/60 px-6 py-20 text-center card-shadow">
      <div class="w-14 h-14 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4">
        <svg class="w-6 h-6 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M20.59 13.41l-7.17 7.17a2 2 0 01-2.83 0L2 12V2h10l8.59 8.59a2 2 0 010 2.82z"/><line x1="7" y1="7" x2="7.01" y2="7"/></svg>
      </div>
      <p class="text-base font-medium text-gray-900">No tags yet</p>
      <p class="text-sm text-gray-500 mt-1">Create your first tag to organize content</p>
    </div>

    <!-- Create/Edit Modal -->
    <Teleport to="body">
      <Transition name="modal">
        <div v-if="showModal" class="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <div class="absolute inset-0 bg-black/30 backdrop-blur-sm" />
          <div class="relative bg-white rounded-2xl shadow-xl w-full max-w-md max-h-[90vh] flex flex-col animate-fade-in">
            <div class="flex items-center justify-between px-6 pt-6 shrink-0">
              <h3 class="text-lg font-semibold text-gray-900">{{ editItem ? 'Edit Tag' : 'New Tag' }} <span v-if="editItem" class="text-sm font-normal text-gray-400">({{ editItem._id }})</span></h3>
              <button @click="showModal = false" class="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100">
                <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>
            <form @submit.prevent="saveTag" class="flex-1 flex flex-col overflow-hidden">
              <div class="flex-1 overflow-y-auto p-6 pt-5 space-y-5">
                <div>
                  <label class="block text-sm font-medium text-gray-600 mb-1.5">Name</label>
                  <input v-model="form.name" type="text" required placeholder="Tag name" class="w-full h-[42px] px-3.5 rounded-lg border border-gray-200 text-sm text-gray-900 placeholder-gray-300 outline-none focus:border-[var(--color-primary)] focus:ring-2 focus:ring-[var(--color-primary-glow)]" />
                </div>
              </div>
              <div class="px-6 py-4 border-t border-gray-100 flex justify-end gap-3 shrink-0">
                <button type="button" @click="showModal = false" class="px-4 py-2 text-sm font-medium text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors">Cancel</button>
                <button type="submit" :disabled="saving" class="px-5 py-2 text-sm font-medium bg-[var(--color-primary)] text-white rounded-lg hover:bg-[var(--color-primary-hover)] transition-colors disabled:opacity-50">
                  {{ saving ? 'Saving...' : 'Save' }}
                </button>
              </div>
            </form>
          </div>
        </div>
      </Transition>
    </Teleport>

    <ConfirmModal :show="showDeleteModal" :loading="deleting" title="Delete Tag" :message="`Delete '${deleteItem?.name}'? This cannot be undone.`" confirm-text="Delete" :danger="true" @confirm="deleteTag" @cancel="showDeleteModal = false" />
  </div>
</template>

<script>
import ListToolbar from '../../components/ListToolbar.vue'
import ActionsMenu from '../../components/ActionsMenu.vue'
import ConfirmModal from '../../components/ConfirmModal.vue'
import icons from '../../components/action-icons.js'
import api from '../../services/api.js'
import { notifySuccess } from '../../services/notifications.js'

export default {
  name: 'TagsList',
  components: { ListToolbar, ActionsMenu, ConfirmModal },
  data() {
    return {
      loading: true, items: [], search: '', sort: 'created:-1',
      sortOptions: [
        { value: 'name:1', label: 'Name A-Z' }, { value: 'name:-1', label: 'Name Z-A' },
        { value: 'created:1', label: 'Created (oldest)' }, { value: 'created:-1', label: 'Created (newest)' }
      ],
      pagination: { limit: 10, page: 1, total: 0 },
      showModal: false, showDeleteModal: false, deleting: false, saving: false,
      editItem: null, deleteItem: null, form: { name: '' }
    }
  },
  watch: {
    search() { this.pagination.page = 1; this.fetchTags() },
    sort() { this.pagination.page = 1; this.fetchTags() }
  },
  mounted() { this.fetchTags() },
  methods: {
    async fetchTags(count = true) {
      try {
        const params = { limit: this.pagination.limit, page: this.pagination.page }
        if (this.search) params.text = this.search
        if (this.sort) params.sort = this.sort
        if (count) params.count = true
        const res = await api.get('/tags', { params })
        this.items = res.data?.items || []
        if (Number.isInteger(res.data?.total)) this.pagination.total = res.data.total
      } catch { /* keep */ } finally { this.loading = false }
    },
    goToPage(page) { this.pagination.page = page; this.fetchTags(false) },
    openCreate() { this.editItem = null; this.form = { name: '' }; this.showModal = true },
    openEdit(item) { this.editItem = item; this.form = { name: item.name }; this.showModal = true },
    async saveTag() {
      this.saving = true
      try {
        if (this.editItem) {
          await api.put(`/tags/${this.editItem._id}`, { tag: this.form })
          notifySuccess('Tag updated')
        } else {
          await api.post('/tags', { tag: this.form })
          notifySuccess('Tag created')
        }
        this.showModal = false; this.fetchTags()
      } catch { /* toast from interceptor */ }
      this.saving = false
    },
    tagActions(item) {
      return [
        { label: 'Edit', show: this.$checkPermission('tags.update'), action: () => this.openEdit(item), icon: icons.edit },
        { divider: true },
        { label: 'Delete', show: this.$checkPermission('tags.delete'), danger: true, action: () => this.confirmDelete(item), icon: icons.delete }
      ]
    },
    confirmDelete(item) { this.deleteItem = item; this.showDeleteModal = true },
    async deleteTag() {
      this.deleting = true
      try {
        await api.delete(`/tags/${this.deleteItem._id}`)
        notifySuccess('Tag deleted'); this.fetchTags()
      } catch { /* toast */ }
      this.showDeleteModal = false; this.deleteItem = null
      this.deleting = false
    },
  }
}
</script>
<style scoped>
.modal-enter-active, .modal-leave-active { transition: opacity 0.2s ease; }
.modal-enter-from, .modal-leave-to { opacity: 0; }
</style>
